/**
 * RightRibbonPanel — Extreme right panel showing:
 *  5 tabs: Approvals | Todo | Quick | Emergency | Reminders
 *  Panel moves tabs to TOP (below user heading), width +15% (w-[332px])
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  User, X, ChevronRight, ChevronLeft, Building2, Calendar, 
  Clock, MapPin, CheckCircle2, Circle, Plus, Trash2, 
  Bell, BellRing, FileText, Settings, LogOut, AlertCircle, AlertTriangle,
  DollarSign, Package, Users, Briefcase, Edit2, Save,
  Loader2, Phone, Zap, Siren, MessageSquare, Shield, ChevronDown,
  Flame, StickyNote, CalendarClock, PhoneCall,
  ClipboardList as ClipboardListIcon, KeyRound, Eye, EyeOff, Search
} from 'lucide-react';
import { useERPContextSafe } from '@/context/ERPContext';
import { useGlobal, useGlobalSnapshot } from '@/store/GlobalStoreProvider';
import { GK } from '@/store/GlobalKeys';
import { API_BASE, getToken } from '@/lib/api';
import { useLicense } from '@/context/LicenseContext';
import CompanySelector from '@/components/report-engine/CompanySelector';
import QuickAccessPanel, { QuickAccessButton } from '@/components/report-engine/QuickAccessPanel';

// ═══════════════════════════════════════════════════════════════════════════
// LICENSE / TIER CARD
// ═══════════════════════════════════════════════════════════════════════════
const LicenseTierCard = ({ isDark }) => {
  const lic = useLicense();
  if (!lic.loaded) return null;
  // Enterprise: never show expired/trial badge — always "Active"
  const isExpired = !lic.accessUnrestricted && (!lic.licensed || lic.status === 'Expired');
  const isTrial   = !lic.accessUnrestricted && lic.status === 'Trial';
  const expiry    = lic.expiryDate ? new Date(lic.expiryDate) : null;
  const cur       = lic.currency === 'INR' ? '₹' : (lic.currency || '');
  const priceText = lic.price === 0 ? 'Free' : `${cur}${(lic.price || 0).toLocaleString('en-IN')} /${lic.billingCycle === 'monthly' ? 'mo' : (lic.billingCycle || 'yr')}`;
  return (
    <div data-testid="ribbon-license-card" className={`mx-4 mb-3 rounded-lg p-3 ${isDark ? 'bg-[#0f1a2e] border border-blue-500/20' : 'bg-blue-50 border border-blue-200'}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className={`text-[9px] font-bold uppercase tracking-wider ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>License</p>
          <p data-testid="ribbon-license-tier" className={`text-xs font-bold mt-0.5 truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{lic.tierName || lic.tier}</p>
        </div>
        <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${
          isExpired ? 'bg-red-500/15 text-red-500' :
          isTrial   ? 'bg-amber-500/15 text-amber-500' :
                      'bg-emerald-500/15 text-emerald-500'
        }`}>{isExpired ? 'Expired' : isTrial ? 'Trial' : 'Live'}</span>
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px]">
        <span className={isDark ? 'text-slate-400' : 'text-slate-600'} data-testid="ribbon-license-price">{priceText}</span>
        <span className={isDark ? 'text-slate-500' : 'text-slate-500'}>
          {isExpired ? 'Renew now' :
            expiry ? `Renews ${expiry.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}` :
            ''}
        </span>
      </div>
      <button
        data-testid="ribbon-license-upgrade-btn"
        onClick={lic.openUpgradeModal}
        className={`mt-2 w-full py-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider transition-colors ${
          isExpired ? 'bg-red-500 text-white hover:bg-red-600' :
                      'bg-blue-600 text-white hover:bg-blue-700'
        }`}
      >
        {isExpired ? 'Renew License' : 'Upgrade Tier'}
      </button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// TODO ITEM COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
const TodoItem = ({ todo, onToggle, onDelete, isDark }) => (
  <div className={`flex items-start gap-2 p-2 rounded-lg group transition-colors ${
    isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'
  }`}>
    <button
      onClick={() => onToggle(todo.id)}
      className={`mt-0.5 shrink-0 ${todo.completed ? 'text-emerald-500' : isDark ? 'text-slate-600' : 'text-slate-300'}`}
    >
      {todo.completed ? <CheckCircle2 size={16} /> : <Circle size={16} />}
    </button>
    <span className={`flex-1 text-xs ${
      todo.completed 
        ? 'line-through ' + (isDark ? 'text-slate-600' : 'text-slate-400')
        : isDark ? 'text-slate-300' : 'text-slate-700'
    }`}>
      {todo.text}
    </span>
    <button
      onClick={() => onDelete(todo.id)}
      className={`opacity-0 group-hover:opacity-100 transition-opacity ${
        isDark ? 'text-slate-600 hover:text-red-400' : 'text-slate-300 hover:text-red-500'
      }`}
    >
      <Trash2 size={12} />
    </button>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// PENDING APPROVAL ITEM
// ═══════════════════════════════════════════════════════════════════════════
const ApprovalItem = ({ approval, isDark, onAction }) => (
  <div className={`p-3 rounded-lg border transition-colors ${
    isDark ? 'bg-[#1a1a1a] border-[#333] hover:border-[#444]' : 'bg-white border-slate-200 hover:border-slate-300'
  }`}>
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-semibold truncate ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>
          {approval.title}
        </p>
        <p className={`text-[10px] mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
          {approval.type} • {approval.date}
        </p>
      </div>
      <div className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${
        approval.priority === 'high' 
          ? 'bg-red-500/10 text-red-500' 
          : approval.priority === 'medium'
            ? 'bg-amber-500/10 text-amber-500'
            : 'bg-blue-500/10 text-blue-500'
      }`}>
        {approval.priority}
      </div>
    </div>
    <div className="flex gap-1.5 mt-2">
      <button 
        onClick={() => onAction(approval.id, 'approve')}
        className="flex-1 px-2 py-1 text-[10px] font-bold uppercase rounded bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 transition-colors"
      >
        Approve
      </button>
      <button 
        onClick={() => onAction(approval.id, 'reject')}
        className="flex-1 px-2 py-1 text-[10px] font-bold uppercase rounded bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors"
      >
        Reject
      </button>
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// QUICK ACTION BUTTON
// ═══════════════════════════════════════════════════════════════════════════
const QuickActionButton = ({ icon: Icon, label, onClick, isDark, color = 'blue' }) => {
  const colorMap = {
    blue: 'bg-blue-500/10 text-blue-500 hover:bg-blue-500/20',
    emerald: 'bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20',
    amber: 'bg-amber-500/10 text-amber-500 hover:bg-amber-500/20',
    purple: 'bg-purple-500/10 text-purple-500 hover:bg-purple-500/20',
  };
  
  return (
    <button
      onClick={onClick}
      className={`flex flex-col items-center gap-1.5 p-3 rounded-lg transition-colors ${colorMap[color]}`}
    >
      <Icon size={18} />
      <span className="text-[9px] font-bold uppercase tracking-tight">{label}</span>
    </button>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// THEME SWATCHES (migrated from UserMenu)
// ═══════════════════════════════════════════════════════════════════════════
const THEME_SWATCHES = [
  { id: 'default',      label: 'White',      header: '#ffffff' },
  { id: 'ex-dark-blue', label: 'Ex-Dark',    header: '#0a1628' },
  { id: 'dark-blue',    label: 'Navy',       header: '#1a365d' },
  { id: 'lite-blue',    label: 'Sky',        header: '#3b82f6' },
  { id: 'hash',         label: 'Slate',      header: '#4a5568' },
  { id: 'light-hash',   label: 'Lt-Slate',   header: '#9ca3af' },
  { id: 'blue',         label: 'Royal Blue', header: '#3b5998' },
];

// ═══════════════════════════════════════════════════════════════════════════
// MAIN RIGHT RIBBON PANEL
// ═══════════════════════════════════════════════════════════════════════════
export default function RightRibbonPanel({ 
  isOpen, 
  onToggle, 
  theme = 'light',
  onLogout,
  onNavigate,
  onCompanyChange,
  headerTheme,
  onHeaderThemeChange,
}) {
  const isDark = theme === 'dark';
  const erpCtx = useERPContextSafe();
  const globalSnap = useGlobalSnapshot();
  
  // User info from GlobalStore
  const userName = useGlobal(GK.USER_NAME, erpCtx.username || 'User');
  const userEmail = useGlobal(GK.USER_EMAIL, '');
  const userRole = useGlobal(GK.USER_ROLE, erpCtx.role || '');
  const userImage = useGlobal(GK.USER_IMAGE, '');
  
  // Company/Branch info
  const companyName = useGlobal(GK.COMPANY_NAME, erpCtx.company?.compName || '');
  const branchName = useGlobal(GK.BRANCH_NAME, erpCtx.currentBranch?.name || '');
  const branchLocation = useGlobal(GK.BRANCH_LOCATION, '');
  
  // Business date & fiscal year
  const businessDate = useGlobal(GK.BUSINESS_DATE_FMT, '');
  const fiscalYear = useGlobal(GK.FISCAL_YEAR, '');
  const bizDayStatus = useGlobal(GK.BIZ_DAY_STATUS, 'open');
  
  // Currency & locale
  const currencySymbol = useGlobal(GK.CURRENCY_SYMBOL, '₹');
  const currencyCode = useGlobal(GK.CURRENCY_CODE, 'INR');
  
  // Local state
  const [todos, setTodos] = useState(() => {
    try { return JSON.parse(localStorage.getItem('retailcliq_todos') || '[]'); } catch { return []; }
  });
  const [newTodo, setNewTodo] = useState('');
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [loadingApprovals, setLoadingApprovals] = useState(false);
  const [activeSection, setActiveSection] = useState('approvals');
  const [isQuickAccessOpen, setIsQuickAccessOpen] = useState(false);

  // Reminders
  const [reminders, setReminders] = useState(() => {
    try { return JSON.parse(localStorage.getItem('retailcliq_reminders') || '[]'); } catch { return []; }
  });
  const [newReminderText, setNewReminderText] = useState('');
  const [newReminderDate, setNewReminderDate] = useState('');

  // Persist
  useEffect(() => { localStorage.setItem('retailcliq_todos', JSON.stringify(todos)); }, [todos]);
  useEffect(() => { localStorage.setItem('retailcliq_reminders', JSON.stringify(reminders)); }, [reminders]);

  // ── Password Lookup Tool state (LICENSOR / SUPER_ADMIN only) ──────────────
  const [showPwdTool, setShowPwdTool] = useState(false);
  const [pwdUsername, setPwdUsername] = useState('');
  const [pwdResult, setPwdResult] = useState(null);
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdError, setPwdError] = useState('');
  const [showPlain, setShowPlain] = useState(false);
  const isLicensorRole = ['LICENSOR', 'SUPER_ADMIN', 'ADMINSUPPORT'].includes(userRole);
  const isFullNetworkRole = ['LICENSOR', 'SUPER_ADMIN'].includes(userRole);
  const showPwdLookup = ['LICENSOR', 'SUPER_ADMIN', 'ADMINSUPPORT', 'LICENSEE', 'MANAGER'].includes(userRole);

  const lookupPassword = useCallback(async () => {
    if (!pwdUsername.trim()) return;
    setPwdLoading(true); setPwdError(''); setPwdResult(null); setShowPlain(false);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/api/auth/user-credential?username=${encodeURIComponent(pwdUsername.trim())}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!data.success) setPwdError(data.message || 'Not found');
      else setPwdResult(data);
    } catch (e) { setPwdError(e.message); }
    setPwdLoading(false);
  }, [pwdUsername]);

  // ── Support Widget Manager state (LICENSOR / SUPER_ADMIN only) ────────────
  const [showWidgetTool, setShowWidgetTool] = useState(false);
  const [widgetKeys, setWidgetKeys] = useState([]);
  const [widgetKeysLoading, setWidgetKeysLoading] = useState(false);
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [issuedKey, setIssuedKey] = useState(null);
  const [widgetError, setWidgetError] = useState('');

  const widgetStandaloneUrl = `${API_BASE}/api/widget-assets/assistant.html`;
  const widgetEmbedSnippet = `<script src="${API_BASE}/api/widget-assets/cliq-assistant.js" data-widget-key="${issuedKey?.key || 'YOUR_WIDGET_KEY'}" async><\/script>`;

  const fetchWidgetKeys = useCallback(async () => {
    setWidgetKeysLoading(true); setWidgetError('');
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/api/admin/widget-keys`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setWidgetKeys(data.data || []);
      else setWidgetError(data.message || 'Failed to load keys');
    } catch (e) { setWidgetError(e.message); }
    setWidgetKeysLoading(false);
  }, []);

  const issueWidgetKey = useCallback(async () => {
    setWidgetError(''); setIssuedKey(null);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/api/admin/widget-keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ label: newKeyLabel.trim() || 'Unnamed Key' })
      });
      const data = await res.json();
      if (data.success) { setIssuedKey({ key: data.key, label: data.label }); setNewKeyLabel(''); fetchWidgetKeys(); }
      else setWidgetError(data.message || 'Failed to issue key');
    } catch (e) { setWidgetError(e.message); }
  }, [newKeyLabel, fetchWidgetKeys]);

  const revokeWidgetKey = useCallback(async (id) => {
    try {
      const token = getToken();
      await fetch(`${API_BASE}/api/admin/widget-keys/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      fetchWidgetKeys();
    } catch (e) { setWidgetError(e.message); }
  }, [fetchWidgetKeys]);

  // ── ERP Screen Scanner "Flash" tool state (LICENSOR / SUPER_ADMIN only) ────
  const [showScannerTool, setShowScannerTool] = useState(false);
  const [scannerRunning, setScannerRunning] = useState(false);
  const [scannerSummary, setScannerSummary] = useState(null);
  const [scannerError, setScannerError] = useState('');

  // ── Client Admin Feature Manager (SUPER_ADMIN only) ─────────────────────
  const [showFeatureMgr, setShowFeatureMgr] = useState(false);
  const [fmClientId, setFmClientId] = useState('');
  const [fmFeatures, setFmFeatures] = useState([]);
  const [fmLoading, setFmLoading] = useState(false);
  const [fmMsg, setFmMsg] = useState('');
  const VALID_FEATURE_CODES = ['DYNAMIC_ATTRIBUTES', 'HELP_ENGINE'];

  const loadFeatures = useCallback(async (cid) => {
    if (!cid) return;
    setFmLoading(true); setFmMsg('');
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/api/admin/client-features?clientId=${encodeURIComponent(cid)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setFmFeatures(data.features || []);
      else setFmMsg(data.message || 'Failed to load');
    } catch (e) { setFmMsg(e.message); }
    setFmLoading(false);
  }, []);

  const toggleFeature = useCallback(async (featureCode, currentEnabled) => {
    setFmMsg('');
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/api/admin/client-features`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ clientId: fmClientId, featureCode, isEnabled: !currentEnabled })
      });
      const data = await res.json();
      if (data.success) { setFmMsg(data.message); loadFeatures(fmClientId); }
      else setFmMsg(data.message || 'Failed to update');
    } catch (e) { setFmMsg(e.message); }
  }, [fmClientId, loadFeatures]);

  const fetchLastScan = useCallback(async () => {
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/api/dev-tools/scan-erp/last`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success && data.summary) setScannerSummary(data.summary);
    } catch (_) { /* silent — no prior scan yet */ }
  }, []);

  const runScan = useCallback(async () => {
    setScannerRunning(true); setScannerError('');
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/api/dev-tools/scan-erp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ minScore: 70 })
      });
      const data = await res.json();
      if (data.success) setScannerSummary(data.summary);
      else setScannerError(data.error || 'Scan failed');
    } catch (e) { setScannerError(e.message); }
    setScannerRunning(false);
  }, []);

  const downloadScanResult = useCallback(async () => {
    setScannerError('');
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/api/dev-tools/scan-erp/download`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) { const d = await res.json().catch(() => ({})); setScannerError(d.error || 'Download failed'); return; }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'erp-scan-result.json';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { setScannerError(e.message); }
  }, []);

  // Fetch pending approvals
  useEffect(() => {
    if (isOpen) {
      fetchPendingApprovals();
    }
  }, [isOpen]);

  useEffect(() => {
    if (showWidgetTool) fetchWidgetKeys();
  }, [showWidgetTool, fetchWidgetKeys]);

  useEffect(() => {
    if (showScannerTool) fetchLastScan();
  }, [showScannerTool, fetchLastScan]);

  const fetchPendingApprovals = async () => {
    setLoadingApprovals(true);
    try {
      const token = getToken();
      const res = await fetch(`${API_BASE}/api/approvals/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPendingApprovals(data.approvals || []);
      }
    } catch (e) {
      // Silent fail — approvals endpoint may not exist
      setPendingApprovals([
        // Mock data for demo
        { id: 1, title: 'Purchase Order #PO2024001', type: 'PO Approval', date: 'Today', priority: 'high' },
        { id: 2, title: 'Leave Request - John Doe', type: 'HR Approval', date: 'Yesterday', priority: 'medium' },
      ]);
    } finally {
      setLoadingApprovals(false);
    }
  };

  const handleAddTodo = () => {
    if (!newTodo.trim()) return;
    setTodos(prev => [
      { id: Date.now(), text: newTodo.trim(), completed: false },
      ...prev
    ]);
    setNewTodo('');
  };

  const handleToggleTodo = (id) => {
    setTodos(prev => prev.map(t => 
      t.id === id ? { ...t, completed: !t.completed } : t
    ));
  };

  const handleDeleteTodo = (id) => {
    setTodos(prev => prev.filter(t => t.id !== id));
  };

  const handleApprovalAction = async (id, action) => {
    // TODO: Implement actual approval API call
    setPendingApprovals(prev => prev.filter(a => a.id !== id));
  };

  // Toggle button (visible when panel is closed)
  if (!isOpen) {
    return (
      <button
        data-testid="ribbon-toggle-open"
        onClick={onToggle}
        className={`fixed right-0 top-[calc(50%+40px)] -translate-y-1/2 z-[98] p-2 rounded-l-lg shadow-lg transition-all ${
          isDark 
            ? 'bg-[#1a1a1a] border border-r-0 border-[#333] hover:bg-[#252525] text-slate-400'
            : 'bg-white border border-r-0 border-slate-200 hover:bg-slate-50 text-slate-600'
        }`}
        title="Open User Panel"
      >
        <ChevronLeft size={16} />
      </button>
    );
  }

  return (
    <div 
      data-testid="right-ribbon-panel"
      className={`fixed right-0 top-0 h-full w-[332px] z-[150] flex flex-col shadow-2xl transition-transform duration-300 ${
        isDark 
          ? 'bg-[#0e0e10] border-l border-[#222]' 
          : 'bg-white border-l border-slate-200'
      }`}
    >
      {/* ── Header: title + close + compact user strip ── */}
      <div className={`shrink-0 border-b ${isDark ? 'border-[#222]' : 'border-slate-200'}`}>
        {/* Title row */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          <div className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ${isDark ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'}`}>
              {userImage
                ? <img src={userImage} alt={userName} className="w-full h-full rounded-full object-cover" />
                : userName.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className={`text-[10px] font-black uppercase tracking-wider truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{userName}</p>
              <p className={`text-[8px] font-semibold uppercase ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{userRole || 'User'}</p>
            </div>
          </div>
          <button data-testid="ribbon-toggle-close" onClick={onToggle}
            className={`p-1 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-slate-500' : 'hover:bg-slate-100 text-slate-400'}`}>
            <X size={15} />
          </button>
        </div>

        {/* ── 5 Tabs — immediately below heading ── */}
        <div className="flex border-t" style={{ borderColor: isDark ? '#1e1e1e' : '#f1f5f9' }}>
          {[
            { id: 'approvals', icon: CheckCircle2, label: 'Approvals', badge: pendingApprovals.length, color: 'text-amber-500' },
            { id: 'todos',     icon: ClipboardListIcon, label: 'Todo',      badge: todos.filter(t => !t.completed).length, color: 'text-blue-500' },
            { id: 'actions',   icon: Zap,           label: 'Quick',     badge: 0, color: 'text-emerald-500' },
            { id: 'emergency', icon: Flame,          label: 'Emergency', badge: 0, color: 'text-red-500' },
            { id: 'reminders', icon: BellRing,       label: 'Remind',    badge: reminders.filter(r => !r.done).length, color: 'text-purple-500' },
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeSection === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveSection(tab.id)}
                data-testid={`ribbon-tab-${tab.id}`}
                title={tab.label}
                className={`flex-1 flex flex-col items-center gap-0.5 py-2 text-[8px] font-bold uppercase tracking-tight transition-colors relative ${
                  isActive
                    ? (isDark ? 'text-white bg-white/5 border-b-2 border-blue-500' : `${tab.color} bg-slate-50 border-b-2 border-blue-500`)
                    : (isDark ? 'text-slate-600 hover:text-slate-400' : 'text-slate-400 hover:text-slate-600')
                }`}>
                <Icon size={13} />
                <span>{tab.label}</span>
                {tab.badge > 0 && (
                  <span className="absolute top-1 right-1 min-w-[14px] h-[14px] flex items-center justify-center rounded-full bg-red-500 text-white text-[8px] font-bold px-0.5">
                    {tab.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Content Area (scrollable — includes license card & theme swatches) ── */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4">
        {/* Pending Approvals */}
        {activeSection === 'approvals' && (
          <div className="space-y-2">
            {loadingApprovals ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={20} className="animate-spin text-blue-500" />
              </div>
            ) : pendingApprovals.length === 0 ? (
              <div className={`text-center py-8 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                <CheckCircle2 size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-xs">No pending approvals</p>
              </div>
            ) : (
              pendingApprovals.map(approval => (
                <ApprovalItem
                  key={approval.id}
                  approval={approval}
                  isDark={isDark}
                  onAction={handleApprovalAction}
                />
              ))
            )}
          </div>
        )}

        {/* Todo List */}
        {activeSection === 'todos' && (
          <div className="space-y-3">
            {/* Add Todo */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newTodo}
                onChange={(e) => setNewTodo(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddTodo()}
                placeholder="Add a task..."
                className={`flex-1 px-3 py-2 text-xs rounded-lg border outline-none transition-colors ${
                  isDark 
                    ? 'bg-[#151515] border-[#333] text-white placeholder:text-slate-600 focus:border-blue-500'
                    : 'bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-blue-500'
                }`}
              />
              <button
                onClick={handleAddTodo}
                disabled={!newTodo.trim()}
                className={`px-3 py-2 rounded-lg transition-colors ${
                  newTodo.trim()
                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                    : isDark ? 'bg-[#222] text-slate-600' : 'bg-slate-100 text-slate-400'
                }`}
              >
                <Plus size={14} />
              </button>
            </div>

            {/* Todo Items */}
            <div className="space-y-1">
              {todos.length === 0 ? (
                <div className={`text-center py-6 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                  <FileText size={24} className="mx-auto mb-2 opacity-50" />
                  <p className="text-xs">No tasks yet</p>
                </div>
              ) : (
                todos.map(todo => (
                  <TodoItem
                    key={todo.id}
                    todo={todo}
                    onToggle={handleToggleTodo}
                    onDelete={handleDeleteTodo}
                    isDark={isDark}
                  />
                ))
              )}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        {activeSection === 'actions' && (
          <div className="space-y-4">
            {/* Company Selector + Quick Access (moved out of the header) */}
            <div className="space-y-2">
              <div className={`text-[8px] font-black uppercase tracking-wider ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>Company &amp; Reports</div>
              <div className="relative">
                <CompanySelector userId="admin" theme={theme} onCompanyChange={onCompanyChange} />
              </div>
              <div className="relative">
                <QuickAccessButton onClick={() => setIsQuickAccessOpen(v => !v)} theme={theme} hasItems={true} />
                <QuickAccessPanel
                  userId="admin"
                  theme={theme}
                  isOpen={isQuickAccessOpen}
                  onClose={() => setIsQuickAccessOpen(false)}
                  onReportSelect={(report) => { if (report.screenId) onNavigate?.(report.screenId); }}
                />
              </div>
            </div>

            {/* Session Info card */}
            <div className={`p-3 rounded-xl text-[10px] space-y-2 ${isDark ? 'bg-[#151515]' : 'bg-slate-50'}`}>
              <div className={`text-[8px] font-black uppercase tracking-wider ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>Session</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { icon: Building2, label: 'Company', value: companyName || 'N/A' },
                  { icon: MapPin,    label: 'Branch',  value: branchName || 'Main' },
                  { icon: Calendar,  label: 'BizDate', value: businessDate || new Date().toLocaleDateString() },
                  { icon: Clock,     label: 'FY',      value: fiscalYear || 'N/A' },
                ].map(({ icon: Icon, label, value }) => (
                  <div key={label} className={`p-2 rounded-lg ${isDark ? 'bg-[#1a1a1a]' : 'bg-white'}`}>
                    <div className="flex items-center gap-1 mb-0.5">
                      <Icon size={8} className={isDark ? 'text-slate-600' : 'text-slate-400'} />
                      <span className={`text-[7px] font-bold uppercase ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>{label}</span>
                    </div>
                    <p className={`text-[9px] font-semibold truncate ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{value}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <span className={`text-[9px] font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{currencySymbol} {currencyCode}</span>
                <span className={`text-[8px] font-bold uppercase px-2 py-0.5 rounded-full ${bizDayStatus === 'open' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`}>
                  {bizDayStatus === 'open' ? 'Day Open' : 'Day Closed'}
                </span>
              </div>
            </div>
            {/* Quick action buttons */}
            <div className="grid grid-cols-2 gap-2">
              <QuickActionButton icon={FileText}  label="New Invoice" onClick={() => onNavigate?.('SALES_INVOICE')}    isDark={isDark} color="blue"    />
              <QuickActionButton icon={Package}   label="Stock Check" onClick={() => onNavigate?.('CLOSING_STOCK')}    isDark={isDark} color="emerald" />
              <QuickActionButton icon={Users}     label="Customers"   onClick={() => onNavigate?.('CUSTOMER_MASTER')}  isDark={isDark} color="purple"  />
              <QuickActionButton icon={Briefcase} label="Reports"     onClick={() => onNavigate?.('HUB')}              isDark={isDark} color="amber"   />
            </div>
          </div>
        )}

        {/* Emergency */}
        {activeSection === 'emergency' && (
          <div className="space-y-3">
            <div className={`p-3 rounded-xl border ${isDark ? 'bg-red-950/30 border-red-900/40' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                <Flame size={14} className="text-red-500" />
                <span className={`text-[10px] font-black uppercase tracking-wider ${isDark ? 'text-red-300' : 'text-red-700'}`}>Emergency Escalation</span>
              </div>
              <p className={`text-[9px] mb-3 ${isDark ? 'text-red-400/80' : 'text-red-600/70'}`}>Instantly alert your team in critical situations</p>
              <div className="space-y-2">
                {[
                  { label: 'SOS — Alert Manager',   icon: AlertTriangle, color: 'red',    action: 'manager_sos'  },
                  { label: 'IT Emergency',           icon: Shield,        color: 'orange', action: 'it_emergency' },
                  { label: 'Security Alert',         icon: Siren,         color: 'amber',  action: 'security_alert' },
                ].map(btn => {
                  const Icon = btn.icon;
                  return (
                    <button key={btn.action}
                      data-testid={`emergency-${btn.action}`}
                      onClick={() => alert(`Emergency escalation: ${btn.label}\nFeature integrates with your ticketing system.`)}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[10px] font-bold uppercase transition-all active:scale-95 ${
                        isDark
                          ? 'bg-red-900/30 text-red-300 border border-red-800/40 hover:bg-red-800/40'
                          : 'bg-red-100 text-red-700 border border-red-200 hover:bg-red-200'
                      }`}>
                      <Icon size={12} />
                      {btn.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className={`p-3 rounded-xl ${isDark ? 'bg-[#151515]' : 'bg-slate-50'}`}>
              <div className={`text-[8px] font-black uppercase tracking-wider mb-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Quick Contacts</div>
              {[
                { name: 'IT Support',     num: '1800-RETAIL-IT' },
                { name: 'Manager',        num: 'Ext 101' },
                { name: 'Security',       num: 'Ext 102' },
              ].map(c => (
                <div key={c.name} className={`flex items-center justify-between py-1.5 border-b last:border-b-0 ${isDark ? 'border-[#252525]' : 'border-slate-100'}`}>
                  <div>
                    <p className={`text-[10px] font-semibold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{c.name}</p>
                    <p className={`text-[8px] ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>{c.num}</p>
                  </div>
                  <button className="p-1.5 rounded-lg bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500/20 transition-colors">
                    <PhoneCall size={10} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Personal Reminders */}
        {activeSection === 'reminders' && (
          <div className="space-y-3">
            {/* Add new reminder */}
            <div className={`p-3 rounded-xl ${isDark ? 'bg-[#151515]' : 'bg-slate-50'} space-y-2`}>
              <div className={`text-[8px] font-black uppercase tracking-wider ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Add Reminder</div>
              <input
                value={newReminderText}
                onChange={e => setNewReminderText(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && newReminderText.trim()) {
                    const nr = { id: Date.now(), text: newReminderText.trim(), date: newReminderDate || null, done: false, createdAt: new Date().toISOString() };
                    setReminders(r => [nr, ...r]);
                    setNewReminderText(''); setNewReminderDate('');
                  }
                }}
                placeholder="Reminder note... (Enter to save)"
                className={`w-full text-[10px] px-2 py-1.5 rounded-lg border outline-none focus:ring-1 focus:ring-purple-500 ${isDark ? 'bg-[#1a1a1a] border-[#333] text-white placeholder:text-slate-600' : 'bg-white border-slate-200 text-slate-800 placeholder:text-slate-400'}`}
              />
              <div className="flex gap-1.5">
                <input type="date" value={newReminderDate} onChange={e => setNewReminderDate(e.target.value)}
                  className={`flex-1 text-[9px] px-2 py-1.5 rounded-lg border outline-none focus:ring-1 focus:ring-purple-500 ${isDark ? 'bg-[#1a1a1a] border-[#333] text-slate-300' : 'bg-white border-slate-200 text-slate-700'}`}
                />
                <button
                  onClick={() => {
                    if (!newReminderText.trim()) return;
                    const nr = { id: Date.now(), text: newReminderText.trim(), date: newReminderDate || null, done: false, createdAt: new Date().toISOString() };
                    setReminders(r => [nr, ...r]); setNewReminderText(''); setNewReminderDate('');
                  }}
                  className="px-3 py-1.5 rounded-lg bg-purple-500 text-white text-[10px] font-bold hover:bg-purple-600 transition-colors flex items-center gap-1">
                  <Plus size={10} /> Add
                </button>
              </div>
            </div>
            {/* Reminder list */}
            <div className="space-y-1.5">
              {reminders.length === 0 ? (
                <div className={`text-center py-6 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                  <BellRing size={24} className="mx-auto mb-2 opacity-50" />
                  <p className="text-xs">No reminders yet</p>
                </div>
              ) : (
                reminders.map(r => (
                  <div key={r.id} className={`flex items-start gap-2 p-2.5 rounded-lg border ${
                    r.done
                      ? isDark ? 'bg-[#111] border-[#1e1e1e] opacity-50' : 'bg-slate-50 border-slate-100 opacity-60'
                      : isDark ? 'bg-[#151515] border-[#222]' : 'bg-white border-slate-200'
                  }`}>
                    <button onClick={() => setReminders(prev => prev.map(x => x.id === r.id ? { ...x, done: !x.done } : x))}
                      className={`mt-0.5 shrink-0 ${r.done ? 'text-emerald-500' : isDark ? 'text-slate-600' : 'text-slate-300'}`}>
                      {r.done ? <CheckCircle2 size={13} /> : <Circle size={13} />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <p className={`text-[10px] font-semibold ${r.done ? 'line-through' : ''} ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{r.text}</p>
                      {r.date && <p className={`text-[8px] mt-0.5 flex items-center gap-1 ${isDark ? 'text-purple-400' : 'text-purple-600'}`}><CalendarClock size={8} /> {r.date}</p>}
                    </div>
                    <button onClick={() => setReminders(prev => prev.filter(x => x.id !== r.id))}
                      className={`shrink-0 p-0.5 rounded transition-colors ${isDark ? 'text-slate-700 hover:text-red-400' : 'text-slate-300 hover:text-red-500'}`}>
                      <Trash2 size={11} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        </div>{/* /p-4 */}

        {/* ── License / Tier Card (scrolls with content) ── */}
        <LicenseTierCard isDark={isDark} />

        {/* ── Theme Swatches (scrolls with content) ── */}
        {onHeaderThemeChange && (
          <div className={`shrink-0 px-4 py-3 border-t ${isDark ? 'border-[#222]' : 'border-slate-200'}`}>
            <p className={`text-[9px] font-bold uppercase tracking-wider mb-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Header Theme</p>
            <div className="flex gap-1.5 flex-wrap">
              {THEME_SWATCHES.map(t => (
                <button key={t.id} onClick={() => onHeaderThemeChange(t.id)} title={t.label}
                  className={`w-6 h-6 rounded-full border-2 transition-all hover:scale-110 ${headerTheme === t.id ? 'border-blue-500 ring-2 ring-blue-400 scale-110' : isDark ? 'border-[#444]' : 'border-slate-300'}`}
                  style={{ background: t.header }} data-testid={`ribbon-theme-${t.id}`} />
              ))}
            </div>
          </div>
        )}
      </div>{/* /scrollable */}

      {/* ── Password Lookup Tool (LICENSEE / MANAGER / ADMINSUPPORT = own company | LICENSOR / SUPER_ADMIN = full network) ── */}
      {showPwdLookup && (
        <div className={`shrink-0 px-4 py-3 border-t ${isDark ? 'border-[#222]' : 'border-slate-200'}`}>
          <button data-testid="pwd-lookup-toggle" onClick={() => { setShowPwdTool(v => !v); setPwdResult(null); setPwdError(''); setPwdUsername(''); setShowPlain(false); }}
            className={`w-full flex items-center gap-2 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors rounded-lg px-2 ${isDark ? 'text-amber-400 hover:bg-amber-400/10' : 'text-amber-600 hover:bg-amber-50'}`}>
            <KeyRound size={12} />
            Password Lookup
            <span className={`ml-auto text-[8px] px-1.5 py-0.5 rounded font-bold ${
              isFullNetworkRole
                ? (isDark ? 'bg-purple-400/20 text-purple-300' : 'bg-purple-100 text-purple-700')
                : (isDark ? 'bg-amber-400/20 text-amber-300' : 'bg-amber-100 text-amber-700')
            }`}>
              {isFullNetworkRole ? 'FULL NETWORK' : 'OWN COMPANY'}
            </span>
          </button>
          {showPwdTool && (
            <div className={`mt-2 p-3 rounded-lg border ${isDark ? 'bg-[#0d0d0d] border-[#2a2a2a]' : 'bg-amber-50 border-amber-200'}`}>
              <p className={`text-[9px] mb-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                {isFullNetworkRole
                  ? 'Search any user across the entire network'
                  : 'Search users within your company only'}
              </p>
              <div className="flex gap-1.5">
                <input data-testid="pwd-lookup-input" value={pwdUsername} onChange={e => setPwdUsername(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && lookupPassword()}
                  placeholder="Enter username..."
                  className={`flex-1 text-[11px] px-2 py-1.5 rounded border outline-none ${isDark ? 'bg-[#1a1a1a] border-[#444] text-white placeholder-slate-600' : 'bg-white border-amber-200 text-slate-800 placeholder-slate-400'}`} />
                <button data-testid="pwd-lookup-go" onClick={lookupPassword} disabled={pwdLoading}
                  className={`px-2.5 py-1.5 rounded text-[10px] font-bold transition-colors ${isDark ? 'bg-amber-500 text-black hover:bg-amber-400' : 'bg-amber-500 text-white hover:bg-amber-600'}`}>
                  {pwdLoading ? <Loader2 size={11} className="animate-spin" /> : <Search size={11} />}
                </button>
              </div>
              {pwdError && <p className="text-[10px] text-red-400 mt-1.5">{pwdError}</p>}
              {pwdResult && (
                <div className={`mt-2 p-2 rounded border text-[10px] ${isDark ? 'bg-[#111] border-[#333]' : 'bg-white border-amber-200'}`}>
                  <p className={`font-bold mb-0.5 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                    {pwdResult.username}
                    <span className={`ml-1 font-normal text-[9px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>({pwdResult.source})</span>
                  </p>
                  {pwdResult.clientId && <p className={`mb-0.5 text-[9px] ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{pwdResult.clientId}</p>}
                  {pwdResult.email && <p className={`mb-1 text-[9px] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>{pwdResult.email}</p>}
                  <div className="flex items-center gap-1.5 mt-1">
                    <span data-testid="pwd-decoded-value" className={`font-mono font-bold tracking-wider flex-1 ${isDark ? 'text-green-400' : 'text-green-700'}`}>
                      {showPlain ? pwdResult.decryptedPassword : '••••••••'}
                    </span>
                    <button onClick={() => setShowPlain(v => !v)} title={showPlain ? 'Hide' : 'Show'} className={`p-0.5 rounded ${isDark ? 'text-slate-500 hover:text-white' : 'text-slate-400 hover:text-slate-700'}`}>
                      {showPlain ? <EyeOff size={11} /> : <Eye size={11} />}
                    </button>
                    <button data-testid="pwd-copy-btn" onClick={() => navigator.clipboard.writeText(pwdResult.decryptedPassword)}
                      className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${isDark ? 'bg-blue-600/30 text-blue-300 hover:bg-blue-600/50' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'}`}>
                      Copy
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Support Widget Manager (LICENSOR / SUPER_ADMIN only) — Cliq Smart Assistant embed ── */}
      {isFullNetworkRole && (
        <div className={`shrink-0 px-4 py-3 border-t ${isDark ? 'border-[#222]' : 'border-slate-200'}`}>
          <button data-testid="widget-manager-toggle" onClick={() => { setShowWidgetTool(v => !v); setIssuedKey(null); setWidgetError(''); }}
            className={`w-full flex items-center gap-2 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors rounded-lg px-2 ${isDark ? 'text-emerald-400 hover:bg-emerald-400/10' : 'text-emerald-600 hover:bg-emerald-50'}`}>
            <MessageSquare size={12} />
            Support Widget Manager
            <span className={`ml-auto text-[8px] px-1.5 py-0.5 rounded font-bold ${isDark ? 'bg-emerald-400/20 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}>
              {widgetKeys.filter(k => k.isActive).length} ACTIVE
            </span>
          </button>
          {showWidgetTool && (
            <div className={`mt-2 p-3 rounded-lg border ${isDark ? 'bg-[#0d0d0d] border-[#2a2a2a]' : 'bg-emerald-50 border-emerald-200'}`}>
              <p className={`text-[9px] mb-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                Issue keys to embed the Cliq Smart Ticket Widget on external sites. Tickets sync live with CSSUPPORT_DEV.
              </p>
              <div className="flex gap-1.5 mb-2">
                <input data-testid="widget-key-label-input" value={newKeyLabel} onChange={e => setNewKeyLabel(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && issueWidgetKey()}
                  placeholder="Key label (e.g. Storefront Site)"
                  className={`flex-1 text-[11px] px-2 py-1.5 rounded border outline-none ${isDark ? 'bg-[#1a1a1a] border-[#444] text-white placeholder-slate-600' : 'bg-white border-emerald-200 text-slate-800 placeholder-slate-400'}`} />
                <button data-testid="widget-key-issue-btn" onClick={issueWidgetKey}
                  className={`px-2.5 py-1.5 rounded text-[10px] font-bold transition-colors ${isDark ? 'bg-emerald-500 text-black hover:bg-emerald-400' : 'bg-emerald-500 text-white hover:bg-emerald-600'}`}>
                  Issue Key
                </button>
              </div>
              {widgetError && <p className="text-[10px] text-red-400 mb-1.5">{widgetError}</p>}
              {issuedKey && (
                <div className={`mb-2 p-2 rounded border text-[10px] ${isDark ? 'bg-[#111] border-[#333]' : 'bg-white border-emerald-200'}`}>
                  <p className={`font-bold mb-1 ${isDark ? 'text-white' : 'text-slate-800'}`}>{issuedKey.label} — copy now, shown once:</p>
                  <div className="flex items-center gap-1.5 mb-2">
                    <span data-testid="widget-key-value" className={`font-mono flex-1 truncate ${isDark ? 'text-green-400' : 'text-green-700'}`}>{issuedKey.key}</span>
                    <button data-testid="widget-key-copy-btn" onClick={() => navigator.clipboard.writeText(issuedKey.key)}
                      className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${isDark ? 'bg-blue-600/30 text-blue-300 hover:bg-blue-600/50' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'}`}>
                      Copy
                    </button>
                  </div>
                  <p className={`font-bold mb-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Embed snippet:</p>
                  <textarea readOnly value={widgetEmbedSnippet} rows={2}
                    className={`w-full text-[9px] font-mono p-1.5 rounded border resize-none ${isDark ? 'bg-[#1a1a1a] border-[#333] text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-600'}`} />
                  <button data-testid="widget-snippet-copy-btn" onClick={() => navigator.clipboard.writeText(widgetEmbedSnippet)}
                    className={`mt-1 text-[9px] px-1.5 py-0.5 rounded font-bold ${isDark ? 'bg-blue-600/30 text-blue-300 hover:bg-blue-600/50' : 'bg-blue-100 text-blue-600 hover:bg-blue-200'}`}>
                    Copy Snippet
                  </button>
                </div>
              )}
              <a href={widgetStandaloneUrl} target="_blank" rel="noreferrer" data-testid="widget-standalone-link"
                className={`block text-center mb-2 text-[9px] font-bold py-1.5 rounded ${isDark ? 'bg-[#1a1a1a] text-emerald-300 hover:bg-[#222]' : 'bg-white text-emerald-700 hover:bg-emerald-100 border border-emerald-200'}`}>
                Open Standalone Widget (Agent / Customer) ↗
              </a>
              <p className={`text-[9px] font-bold uppercase mb-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Issued Keys</p>
              {widgetKeysLoading ? (
                <p className={`text-[9px] ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>Loading…</p>
              ) : widgetKeys.length === 0 ? (
                <p className={`text-[9px] ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>No keys issued yet.</p>
              ) : (
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {widgetKeys.map(k => (
                    <div key={k.id} data-testid={`widget-key-row-${k.id}`} className={`flex items-center gap-1.5 text-[9px] p-1.5 rounded ${isDark ? 'bg-[#111]' : 'bg-white border border-emerald-100'}`}>
                      <span className={`flex-1 truncate font-bold ${k.isActive ? (isDark ? 'text-white' : 'text-slate-800') : 'text-slate-500 line-through'}`}>{k.label}</span>
                      <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>{k.usageCount || 0} uses</span>
                      {k.isActive && (
                        <button data-testid={`widget-key-revoke-${k.id}`} onClick={() => revokeWidgetKey(k.id)} className="text-red-400 hover:text-red-300 font-bold">Revoke</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── ERP Screen Scanner "Flash" tool (LICENSOR / SUPER_ADMIN only) ── */}
      {isFullNetworkRole && (
        <div className={`shrink-0 px-4 py-3 border-t ${isDark ? 'border-[#222]' : 'border-slate-200'}`}>
          <button data-testid="scanner-tool-toggle" onClick={() => { setShowScannerTool(v => !v); setScannerError(''); }}
            className={`w-full flex items-center gap-2 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors rounded-lg px-2 ${isDark ? 'text-amber-400 hover:bg-amber-400/10' : 'text-amber-600 hover:bg-amber-50'}`}>
            <Zap size={12} />
            ERP Screen Scanner
            {scannerSummary && (
              <span className={`ml-auto text-[8px] px-1.5 py-0.5 rounded font-bold ${isDark ? 'bg-amber-400/20 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
                {scannerSummary.matchRatePct}% MAPPED
              </span>
            )}
          </button>
          {showScannerTool && (
            <div className={`mt-2 p-3 rounded-lg border ${isDark ? 'bg-[#0d0d0d] border-[#2a2a2a]' : 'bg-amber-50 border-amber-200'}`}>
              <p className={`text-[9px] mb-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                Rescans the frontend source against UAMS_Screens and updates the component-to-screen mapping used for coverage tracking.
              </p>
              <button data-testid="scanner-run-btn" onClick={runScan} disabled={scannerRunning}
                className={`w-full flex items-center justify-center gap-1.5 py-1.5 rounded text-[10px] font-bold transition-colors ${scannerRunning ? 'opacity-60 cursor-not-allowed' : ''} ${isDark ? 'bg-amber-500 text-black hover:bg-amber-400' : 'bg-amber-500 text-white hover:bg-amber-600'}`}>
                {scannerRunning ? <Loader2 size={12} className="animate-spin" /> : <Zap size={12} />}
                {scannerRunning ? 'Scanning…' : 'Flash / Rescan Now'}
              </button>
              {scannerError && <p data-testid="scanner-error" className="text-[10px] text-red-400 mt-1.5">{scannerError}</p>}
              {scannerSummary && (
                <div data-testid="scanner-summary" className={`mt-2 grid grid-cols-2 gap-1.5 text-[10px] ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                  <div className={`p-1.5 rounded ${isDark ? 'bg-[#111]' : 'bg-white border border-amber-100'}`}>
                    Files scanned<br /><span className="font-bold">{scannerSummary.filesScanned}</span>
                  </div>
                  <div className={`p-1.5 rounded ${isDark ? 'bg-[#111]' : 'bg-white border border-amber-100'}`}>
                    UAMS screens<br /><span className="font-bold">{scannerSummary.screensTotal}</span>
                  </div>
                  <div className={`p-1.5 rounded ${isDark ? 'bg-[#111]' : 'bg-white border border-amber-100'}`}>
                    Mapped (≥{scannerSummary.minScore}%)<br /><span className="font-bold text-emerald-400">{scannerSummary.screensMapped}</span>
                  </div>
                  <div className={`p-1.5 rounded ${isDark ? 'bg-[#111]' : 'bg-white border border-amber-100'}`}>
                    Unmapped<br /><span className="font-bold text-red-400">{scannerSummary.screensUnmapped}</span>
                  </div>
                </div>
              )}
              {scannerSummary?.generatedAt && (
                <p className={`text-[8px] mt-1.5 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                  Last run: {new Date(scannerSummary.generatedAt).toLocaleString()}
                </p>
              )}
              {scannerSummary && (
                <button data-testid="scanner-download-btn" onClick={downloadScanResult}
                  className={`w-full mt-1.5 text-[9px] font-bold py-1.5 rounded ${isDark ? 'bg-[#1a1a1a] text-amber-300 hover:bg-[#222]' : 'bg-white text-amber-700 hover:bg-amber-100 border border-amber-200'}`}>
                  Download Mapping JSON ({scannerSummary.screensMapped} screens)
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Client Admin Feature Manager (SUPER_ADMIN only) ── */}
      {userRole === 'SUPER_ADMIN' && (
        <div className={`shrink-0 px-4 py-3 border-t ${isDark ? 'border-[#222]' : 'border-slate-200'}`}>
          <button data-testid="feature-mgr-toggle"
            onClick={() => { setShowFeatureMgr(v => !v); setFmMsg(''); setFmFeatures([]); setFmClientId(''); }}
            className={`w-full flex items-center gap-2 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors rounded-lg px-2 ${isDark ? 'text-emerald-400 hover:bg-emerald-400/10' : 'text-emerald-600 hover:bg-emerald-50'}`}>
            <Shield size={12} />
            Client Admin Features
          </button>
          {showFeatureMgr && (
            <div className={`mt-2 p-3 rounded-lg border space-y-2 ${isDark ? 'bg-[#0d0d0d] border-[#2a2a2a]' : 'bg-emerald-50 border-emerald-200'}`}>
              <p className={`text-[9px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                Grant or revoke super-admin features for a company's admin users (scoped to their company only).
              </p>
              <div className="flex gap-1.5">
                <input
                  data-testid="fm-client-id-input"
                  value={fmClientId}
                  onChange={e => setFmClientId(e.target.value)}
                  placeholder="Client ID (e.g. IN_EXP_0002)"
                  className={`flex-1 text-[10px] px-2 py-1.5 rounded border outline-none focus:ring-1 focus:ring-emerald-500 ${isDark ? 'bg-[#1a1a1a] border-[#333] text-white placeholder:text-slate-600' : 'bg-white border-slate-200 text-slate-800 placeholder:text-slate-400'}`}
                />
                <button data-testid="fm-load-btn"
                  onClick={() => loadFeatures(fmClientId)}
                  disabled={fmLoading || !fmClientId.trim()}
                  className={`px-2 py-1.5 rounded text-[10px] font-bold transition-colors ${fmLoading || !fmClientId.trim() ? 'opacity-50 cursor-not-allowed' : ''} ${isDark ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`}>
                  {fmLoading ? <Loader2 size={10} className="animate-spin" /> : <Search size={10} />}
                </button>
              </div>
              {fmFeatures.length > 0 && (
                <div className="space-y-1.5">
                  {VALID_FEATURE_CODES.map(code => {
                    const row = fmFeatures.find(f => f.FeatureCode === code);
                    const enabled = row ? !!row.IsEnabled : false;
                    return (
                      <div key={code} className={`flex items-center justify-between px-2 py-1.5 rounded border ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-slate-100'}`}>
                        <span className={`text-[9px] font-mono font-bold ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{code}</span>
                        <button data-testid={`fm-toggle-${code}`}
                          onClick={() => toggleFeature(code, enabled)}
                          className={`text-[9px] font-bold px-2 py-0.5 rounded-full transition-colors ${
                            enabled
                              ? isDark ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                              : isDark ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30' : 'bg-red-100 text-red-600 hover:bg-red-200'
                          }`}>
                          {enabled ? 'ON' : 'OFF'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {fmFeatures.length === 0 && fmClientId && !fmLoading && (
                <p className={`text-[9px] text-center py-2 ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                  No features found. Load a valid Client ID first.
                </p>
              )}
              {fmMsg && (
                <p data-testid="fm-message" className={`text-[9px] font-semibold ${fmMsg.startsWith('F') || fmMsg.startsWith('f') ? 'text-red-400' : 'text-emerald-400'}`}>
                  {fmMsg}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Footer Actions — always visible at bottom ── */}
      <div className={`shrink-0 p-4 border-t ${isDark ? 'border-[#222]' : 'border-slate-200'}`}>
        <div className="flex gap-2">
          <button
            onClick={() => onNavigate?.('USER_PROFILES')}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[10px] font-bold uppercase transition-colors ${
              isDark 
                ? 'bg-[#151515] text-slate-400 hover:bg-[#1a1a1a] hover:text-white'
                : 'bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            <Settings size={12} />
            Settings
          </button>
          <button
            data-testid="ribbon-logout-btn"
            onClick={onLogout}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[10px] font-bold uppercase bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors"
          >
            <LogOut size={12} />
            Logout
          </button>
        </div>
      </div>
    </div>
  );
}
